import { useEffect, useRef } from "react";

const OVERSCROLL_THRESHOLD = 80;
const SCROLL_CONTAINER_ID = "gcModalScrollContainer";

/**
 * Closes a modal when the user overscrolls past the top of the scroll container.
 * Supports both touch (swipe down) and wheel (scroll up) events.
 */
export function useOverscrollClose({
  enabled,
  onClose,
}: {
  enabled: boolean;
  onClose?: () => void;
}) {
  const touchStartY = useRef(0);
  const wheelAccumulator = useRef(0);

  useEffect(() => {
    if (!enabled || !onClose) return;

    const scrollContainer = document.getElementById(SCROLL_CONTAINER_ID);
    if (!scrollContainer) return;

    const onTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (scrollContainer.scrollTop > 0) return;
      const deltaY = e.touches[0].clientY - touchStartY.current;
      if (deltaY > OVERSCROLL_THRESHOLD) {
        onClose();
      }
    };

    const onWheel = (e: WheelEvent) => {
      if (scrollContainer.scrollTop > 0) {
        wheelAccumulator.current = 0;
        return;
      }
      if (e.deltaY < 0) {
        wheelAccumulator.current += Math.abs(e.deltaY);
        if (wheelAccumulator.current > OVERSCROLL_THRESHOLD) {
          onClose();
        }
      } else {
        wheelAccumulator.current = 0;
      }
    };

    scrollContainer.addEventListener("touchstart", onTouchStart, {
      passive: true,
    });
    scrollContainer.addEventListener("touchmove", onTouchMove, {
      passive: true,
    });
    scrollContainer.addEventListener("wheel", onWheel, { passive: true });

    return () => {
      scrollContainer.removeEventListener("touchstart", onTouchStart);
      scrollContainer.removeEventListener("touchmove", onTouchMove);
      scrollContainer.removeEventListener("wheel", onWheel);
    };
  }, [enabled, onClose]);
}
